import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Award, FileText, FileSpreadsheet, Upload, AlertTriangle, CheckCircle2, Download, X } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import CertificateTemplateModal from '../components/certificate/CertificateTemplateModal';
import CertificatePreview from '../components/certificate/CertificatePreview';
import { isPortraitTemplate } from '../components/certificate/templateConfigs';
import {
  parsePastClassRoster,
  downloadRosterTemplate,
  type NormalizedRosterRow,
  type RosterDuplicate,
} from '../utils/pastClassRosterUtils';
import type { LocationType, ClassType } from '../types/class';

interface PastClassForm {
  title: string;
  endDate: string; // 발급일 (수업 종료일로 저장)
  locationType: LocationType;
  offlineHours: number;
  onlineHours: number;
  classType: ClassType;
}

// 명단 검증 결과(서버) — 동명이인 등
interface AmbiguousEntry { name: string; phone: string; count: number }

export default function ProviderPastClassCreatePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 인증서 템플릿 상태
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [certificateTemplateId, setCertificateTemplateId] = useState<string | null>(null);
  const [savedTemplateData, setSavedTemplateData] = useState<any>(null);

  // 엑셀 명단 상태
  const [rosterRows, setRosterRows] = useState<NormalizedRosterRow[]>([]);
  const [rosterFileName, setRosterFileName] = useState<string | null>(null);
  const [rosterChecking, setRosterChecking] = useState(false);
  const [rosterError, setRosterError] = useState<{
    title: string;
    internalDuplicates?: RosterDuplicate[];
    ambiguous?: AmbiguousEntry[];
    invalid?: { rowNumber: number; reason: string }[];
  } | null>(null);

  const [formData, setFormData] = useState<PastClassForm>({
    title: '',
    endDate: '',
    locationType: 'OFFLINE',
    offlineHours: 0,
    onlineHours: 0,
    classType: 'CERTIFICATE',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 총 수업시간 검증 후 인증서 템플릿 모달 열기
  const handleOpenTemplateModal = () => {
    const { locationType, offlineHours, onlineHours } = formData;
    if (locationType === 'OFFLINE' && !offlineHours) {
      alert('총 수업 시간(오프라인)을 먼저 입력해주세요.');
      return;
    }
    if (locationType === 'ONLINE' && !onlineHours) {
      alert('총 수업 시간(온라인)을 먼저 입력해주세요.');
      return;
    }
    if (locationType === 'HYBRID' && (!offlineHours || !onlineHours)) {
      alert('총 수업 시간(오프라인, 온라인)을 모두 먼저 입력해주세요.');
      return;
    }
    setIsTemplateModalOpen(true);
  };

  const handleSaveTemplate = (templateData: any, templateId: string) => {
    setCertificateTemplateId(templateId);
    setSavedTemplateData(templateData);
    setIsTemplateModalOpen(false);
  };

  // 엑셀 파일 선택 → 파싱 → 서버 검증
  const handleRosterFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일 재선택 허용
    if (!file) return;

    setRosterError(null);
    setRosterRows([]);
    setRosterFileName(null);
    setRosterChecking(true);

    try {
      const parsed = await parsePastClassRoster(file);

      // 1) 무효 행
      if (parsed.invalidRows.length > 0) {
        setRosterError({
          title: '휴대폰 번호 형식이 올바르지 않은 행이 있습니다. 엑셀을 수정 후 다시 업로드해주세요.',
          invalid: parsed.invalidRows,
        });
        return;
      }

      // 2) 엑셀 내부 중복 (액션 3)
      if (parsed.internalDuplicates.length > 0) {
        setRosterError({
          title: '엑셀에 이름과 전화번호가 모두 동일한 중복 항목이 있습니다. 중복을 제거 후 다시 업로드해주세요.',
          internalDuplicates: parsed.internalDuplicates,
        });
        return;
      }

      if (parsed.rows.length === 0) {
        setRosterError({ title: '유효한 수강자가 없습니다. 엑셀 내용을 확인해주세요.' });
        return;
      }

      // 3) 서버 검증 (기존 회원 동명이인+동일번호 차단)
      const res = await fetch('/api/v1/provider/roster/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ roster: parsed.rows }),
      });
      const data = await res.json();

      if (!res.ok) {
        setRosterError({ title: data.error || '명단 검증에 실패했습니다.' });
        return;
      }

      if (!data.ok) {
        setRosterError({
          title: '명단에 자동 발급이 불가능한 항목이 있어 업로드할 수 없습니다.',
          internalDuplicates: data.internalDuplicates,
          ambiguous: data.ambiguous,
        });
        return;
      }

      // 통과
      setRosterRows(parsed.rows);
      setRosterFileName(file.name);
    } catch (err: any) {
      setRosterError({ title: err.message || '엑셀 파일 처리에 실패했습니다.' });
    } finally {
      setRosterChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError('수업명을 입력해주세요.');
      return;
    }
    if (!formData.endDate) {
      setError('발급일을 선택해주세요.');
      return;
    }
    if (!certificateTemplateId) {
      setError('인증서 템플릿을 만들어주세요.');
      return;
    }
    if (rosterRows.length === 0) {
      setError('과거 수업 수강 인원(엑셀)을 업로드해주세요.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/v1/provider/classes/past', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          endDate: formData.endDate,
          locationType: formData.locationType,
          offlineHours: formData.offlineHours,
          onlineHours: formData.onlineHours,
          classType: formData.classType,
          certificateTemplateId,
          roster: rosterRows,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.code === 'DUPLICATE_ROSTER') {
          setRosterError({
            title: '명단에 중복 또는 동명이인이 있어 등록할 수 없습니다.',
            internalDuplicates: data.internalDuplicates,
            ambiguous: data.ambiguous,
          });
          setRosterRows([]);
          setRosterFileName(null);
          throw new Error('수강 명단을 다시 업로드해주세요.');
        }
        throw new Error(data.error || '과거 수업 등록에 실패했습니다.');
      }

      const enrolled = data.enrolledCount ?? 0;
      const pending = data.pendingCount ?? 0;
      alert(
        `과거 수업이 등록되었습니다.\n- 기존 회원 인증서 즉시 발급: ${enrolled}명\n- 미가입자(가입 시 자동 발급 예정): ${pending}명`
      );
      navigate('/provider/posts');
    } catch (err: any) {
      setError(err.message || '과거 수업 등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const totalHours = (formData.offlineHours || 0) + (formData.onlineHours || 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />

        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div className="w-full">
            <div className="mb-6">
              <button
                onClick={() => navigate('/provider/posts')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">게시물 목록으로 돌아가기</span>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">과거 수업 등록</h1>
              <p className="text-gray-600 mt-1 text-sm">
                이미 종료된 수업의 수강생 명단을 업로드해 인증서를 소급 발급합니다
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-soft overflow-hidden">
              <div className="grid grid-cols-2 gap-6 p-5">
                {/* 첫 번째 열: 수업 정보 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    수업 정보
                  </h3>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      수업명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="예: 필라테스 지도자 자격증 과정 (1기)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      발급일 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">인증서에 표기되는 발급(수료) 일자입니다</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      수업 장소 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="locationType"
                      value={formData.locationType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      required
                    >
                      <option value="OFFLINE">오프라인</option>
                      <option value="ONLINE">온라인</option>
                      <option value="HYBRID">혼합 (온라인 + 오프라인)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      총 수업 시간 (시간) <span className="text-red-500">*</span>
                    </label>
                    <div className={`grid ${formData.locationType === 'HYBRID' ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                      {formData.locationType !== 'ONLINE' && (
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">오프라인 시간</label>
                          <input
                            type="text"
                            value={formData.offlineHours || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setFormData((prev) => ({ ...prev, offlineHours: value === '' ? 0 : parseInt(value) }));
                              }
                            }}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                            required
                          />
                        </div>
                      )}
                      {formData.locationType !== 'OFFLINE' && (
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">온라인 시간</label>
                          <input
                            type="text"
                            value={formData.onlineHours || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                setFormData((prev) => ({ ...prev, onlineHours: value === '' ? 0 : parseInt(value) }));
                              }
                            }}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                            required
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      수업 유형 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="classType"
                      value={formData.classType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      required
                    >
                      <option value="CERTIFICATE">자격증</option>
                      <option value="COMPLETION">수료증</option>
                      <option value="WORKSHOP">워크샵</option>
                      <option value="OTHER">기타</option>
                    </select>
                  </div>

                </div>

                {/* 두 번째 열: 수강 명단 + 인증서 */}
                <div className="space-y-6">
                  {/* 과거 수업 수강 인원 등록 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5" />
                      과거 수업 수강 인원 등록 <span className="text-red-500 text-base">*</span>
                    </h3>

                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600">
                        엑셀 컬럼: <strong>수강자</strong>, <strong>휴대폰 번호</strong>
                      </p>
                      <button
                        type="button"
                        onClick={downloadRosterTemplate}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Download className="w-3.5 h-3.5" />
                        양식 다운로드
                      </button>
                    </div>

                    {rosterRows.length > 0 ? (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="text-sm font-semibold text-green-800">
                              총 {rosterRows.length}명 업로드 완료
                            </p>
                            <p className="text-xs text-green-700">{rosterFileName}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setRosterRows([]);
                            setRosterFileName(null);
                          }}
                          className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                        >
                          <X className="w-3.5 h-3.5" />
                          제거
                        </button>
                      </div>
                    ) : (
                      <label className={`block rounded-lg border-2 border-dashed transition-colors cursor-pointer ${rosterChecking ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-primary bg-gray-50 hover:bg-gray-100'}`}>
                        <div className="py-8 flex flex-col items-center justify-center gap-2">
                          {rosterChecking ? (
                            <>
                              <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                              <span className="text-sm font-medium text-gray-600">명단 검증 중...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-gray-400" />
                              <span className="text-sm font-medium text-gray-600">엑셀 파일 업로드 (.xlsx)</span>
                              <span className="text-xs text-gray-500">모집 인원은 명단 수로 자동 설정됩니다</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleRosterFileSelect}
                          className="hidden"
                          disabled={rosterChecking}
                        />
                      </label>
                    )}

                    {/* 명단 오류/중복 모달성 인라인 표시 */}
                    {rosterError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start gap-2 mb-2">
                          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm font-semibold text-red-800">{rosterError.title}</p>
                        </div>
                        {rosterError.internalDuplicates && rosterError.internalDuplicates.length > 0 && (
                          <div className="mt-2 ml-7">
                            <p className="text-xs font-medium text-red-700 mb-1">엑셀 내 중복:</p>
                            <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
                              {rosterError.internalDuplicates.map((d, i) => (
                                <li key={i}>
                                  {d.name} ({d.phone})
                                  {d.rowNumbers ? ` — ${d.rowNumbers.length}회 (행 ${d.rowNumbers.join(', ')})` : ''}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {rosterError.ambiguous && rosterError.ambiguous.length > 0 && (
                          <div className="mt-2 ml-7">
                            <p className="text-xs font-medium text-red-700 mb-1">동명이인+동일번호 회원 존재:</p>
                            <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
                              {rosterError.ambiguous.map((d, i) => (
                                <li key={i}>{d.name} ({d.phone}) — {d.count}명</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {rosterError.invalid && rosterError.invalid.length > 0 && (
                          <div className="mt-2 ml-7">
                            <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
                              {rosterError.invalid.slice(0, 10).map((d, i) => (
                                <li key={i}>행 {d.rowNumber}: {d.reason}</li>
                              ))}
                              {rosterError.invalid.length > 10 && (
                                <li>외 {rosterError.invalid.length - 10}건...</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 인증서 섹션 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      인증서
                    </h3>

                    {!savedTemplateData ? (
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                          인증서 템플릿 <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleOpenTemplateModal}
                          className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors flex items-center justify-center gap-2 text-gray-700 hover:text-primary text-sm"
                        >
                          <Award className="w-4 h-4" />
                          인증서 템플릿 만들기
                        </button>
                      </div>
                    ) : (
                      <div className="bg-white border-2 border-green-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-semibold text-green-800">{savedTemplateData.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={handleOpenTemplateModal}
                            className="text-xs text-primary hover:underline"
                          >
                            수정
                          </button>
                        </div>
                        {/* 저장 시 캡처한 썸네일 이미지로 표시 (라이브 스케일 렌더의 vw 왜곡·잘림 방지).
                            썸네일이 없으면 기존 라이브 프리뷰로 폴백 */}
                        {savedTemplateData.thumbnailUrl ? (
                          <img
                            src={savedTemplateData.thumbnailUrl}
                            alt="인증서 미리보기"
                            className="w-full rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="overflow-hidden" style={{ height: isPortraitTemplate(savedTemplateData.templateType || 1) ? '500px' : '353.5px' }}>
                            <div className="scale-[0.5] origin-top-left" style={{ width: '200%' }}>
                              <CertificatePreview
                                templateType={savedTemplateData.templateType || 1}
                                borderColor={savedTemplateData.borderColor}
                                borderThickness={savedTemplateData.borderThickness}
                                logoUrl={savedTemplateData.logoUrl}
                                logoPosition={savedTemplateData.logoPosition}
                                logoSize={savedTemplateData.logoSize}
                                onLogoPositionChange={() => {}}
                                onLogoSizeChange={() => {}}
                                signatureUrl={savedTemplateData.signatureUrl}
                                signatureUrl2={savedTemplateData.signatureUrl2}
                                signaturePosition={savedTemplateData.signaturePosition}
                                signatureSize={savedTemplateData.signatureSize}
                                onSignaturePositionChange={() => {}}
                                onSignatureSizeChange={() => {}}
                                courseName={savedTemplateData.courseName}
                                signerName={savedTemplateData.signerName}
                                signerTitle={savedTemplateData.signerTitle}
                                signerName2={savedTemplateData.signerName2}
                                totalHours={totalHours}
                                captureMode={true}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-5 bg-gray-50 flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/provider/posts')}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors font-medium text-sm"
                  disabled={loading}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      등록 중...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      과거 수업 등록
                    </>
                  )}
                </button>
              </div>
            </form>

            <CertificateTemplateModal
              isOpen={isTemplateModalOpen}
              onClose={() => setIsTemplateModalOpen(false)}
              onSave={handleSaveTemplate}
              existingTemplateId={certificateTemplateId}
              initialData={savedTemplateData}
              totalHours={totalHours}
            />
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
